import { GridLayout, ParticipantTile, useTrackRefContext, useTracks } from '@livekit/components-react';
import { Track } from 'livekit-client';
import { useEffect, useState } from 'react';

import { useStrings } from '../i18n/context';
import type { ListeningMode } from '../protocol';
import { captionOn, nextCaptionExpiry, type SubtitleLine } from '../streams';

export interface Listener {
  readonly identity: string;
  readonly language: string;
}

interface Props {
  readonly lines: readonly SubtitleLine[];
  readonly listener: Listener;
  /** In "off" no caption is shown (ADR 001, the listener rule). */
  readonly mode: ListeningMode;
}

/**
 * A clock that ticks exactly when the next visible caption expires. Between ticks `now` may
 * lag the real time: a caption then stays a moment longer, never shorter, and the tick that
 * follows is scheduled from the real time.
 */
function useCaptionClock(lines: readonly SubtitleLine[], listener: Listener): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const next = nextCaptionExpiry(lines, listener, now);
    if (next === null) return undefined;
    const id = window.setTimeout(() => setNow(Date.now()), Math.max(0, next - Date.now()) + 50);
    return () => window.clearTimeout(id);
  }, [lines, listener, now]);
  return now;
}

/**
 * Every person's camera (a placeholder when it is off) and every screen share, each camera
 * tile carrying the caption of what that person says for a listener of another language.
 * The interpreter is an agent participant with no camera: it gets no tile.
 */
export function VideoGrid({ lines, listener, mode }: Props) {
  const tracks = useTracks(
    [
      { source: Track.Source.Camera, withPlaceholder: true },
      { source: Track.Source.ScreenShare, withPlaceholder: false },
    ],
    { onlySubscribed: false },
  );
  const people = tracks.filter((track) => !track.participant.isAgent);
  const now = useCaptionClock(lines, listener);
  return (
    <GridLayout tracks={people} className="meeting__grid">
      <Tile lines={lines} listener={listener} mode={mode} now={now} />
    </GridLayout>
  );
}

/**
 * One tile: the person, and under their picture the latest sentence they said, translated into
 * the listener's language, for a reading pace (streams.ts: captionOn). Speech in the
 * listener's own language gets no caption. Not a live region: interim text changes many times
 * a second; the transcript panel carries the text for assistive technology.
 */
function Tile({ lines, listener, mode, now }: Props & { readonly now: number }) {
  const strings = useStrings();
  const trackRef = useTrackRefContext();
  const caption =
    mode === 'off' || trackRef.source !== Track.Source.Camera
      ? null
      : captionOn(lines, trackRef.participant.identity, listener, now);
  return (
    <div className="tile">
      <ParticipantTile trackRef={trackRef} />
      {caption && (
        <p className="tile__caption" aria-hidden="true">
          {caption.unclear ? (
            <span className="tile__caption-note">{strings.subtitleUnclear}</span>
          ) : caption.translation ? (
            <span lang={listener.language}>{caption.translation}</span>
          ) : (
            <span className="tile__caption-note">{strings.subtitleTranslating}</span>
          )}
        </p>
      )}
    </div>
  );
}
